import { Injectable, inject } from '@angular/core';
import { Erzeugungsanlage } from '../../domain/model/erzeugungsanlage';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs/internal/Observable';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { ErzeugungsanlageFilter } from './erzeugungsanlage-filter';
import { InstallierteLeistungAggregate } from '../../domain/model/report/installierte-leistung-aggregate';
import { Pageable } from '../../common/paging/pageable';

export interface Range {
    min: number;
    max: number;
}

// TODO(rb) move
export class Page<T> {
    content: T[];
    first: boolean;
    last: boolean;
    number: number;
    numberOfElements: number;
    size: number;
    // sort: {sorted: true, unsorted: false}
    totalElements: number;
    totalPages: number;

    installierteLeistungMax: number;
    installierteLeistungMin: number;
}

@Injectable({
    providedIn: 'root',
})
export class ErzeugungsanlageService {
    private http = inject(HttpClient);

    private static createDefaultPageable(): Pageable {
        let pageable: Pageable;
        pageable = new Pageable();
        pageable.size = 50;
        pageable.page = 0;
        return pageable;
    }

    /**
     * Gets an Erzeugungsanlage identified by its unique Erzeugungsanlage.westnetzId
     * @param {string} westnetzId
     * @returns {Observable<Erzeugungsanlage>}
     */
    public getErzeugungsanlage(westnetzId: string): Observable<Erzeugungsanlage> {
        return this.http.get<Erzeugungsanlage>(environment.API_URL + '/erzeugungsanlage/' + westnetzId);
    }

    public getErzeugungsanlagen(
        pageable?: Pageable,
        filter?: ErzeugungsanlageFilter,
    ): Observable<Page<Erzeugungsanlage>> {
        const params = this.toHttpParams(pageable, filter);
        return this.http.get(environment.API_URL + '/erzeugungsanlage', { params }) as Observable<Page<Erzeugungsanlage>>;
    }

    public getInstallierteLeistungRange(stadtwerkeId?: string): Observable<Range> {
        return this.getErzeugungsanlagen(
            { size: 1 } as Pageable,
            { stadtwerkeId })
            .pipe(map((page: Page<Erzeugungsanlage>) => {
                return {
                    min: page.installierteLeistungMin,
                    max: page.installierteLeistungMax,
                };
            }));
    }

    public createSummenerzeuger(stadtwerkeId: string, erzeugungsanlage: Erzeugungsanlage): Observable<Erzeugungsanlage> {
        let params = new HttpParams();
        params = params.set('stadtwerkeId', stadtwerkeId);
        // TODO assert westnetzId is not set
        return this.http.post<Erzeugungsanlage>(
            environment.API_URL + '/erzeugungsanlage/summenerzeuger',
            erzeugungsanlage,
            { params },
        );
    }

    public saveSummenerzeuger(stadtwerkeId: string, erzeugungsanlage: Erzeugungsanlage): Observable<Erzeugungsanlage> {
        let params = new HttpParams();
        params = params.set('stadtwerkeId', stadtwerkeId);
        // TODO assert westnetzId is set
        return this.http.put<Erzeugungsanlage>(
            environment.API_URL + '/erzeugungsanlage/summenerzeuger/' + erzeugungsanlage.westnetzId,
            erzeugungsanlage,
            { params },
        );
    }

    public deleteErzeugungsanlage(westnetzId: string): Observable<any> {
        return this.http.delete(environment.API_URL + '/erzeugungsanlage/' + westnetzId) as Observable<any>;
    }

    public getErzeugungsanlageWithAnlagentypAndNetzzuordnung(anlagentypnummer: string, netzzuordnungId: string,
        stadtwerkeId: string): Observable<Erzeugungsanlage> {
        const filter: ErzeugungsanlageFilter = { anlagentypnummer, netzzuordnungId, stadtwerkeId };
        return this.getErzeugungsanlagen(ErzeugungsanlageService.createDefaultPageable(), filter).pipe(map(page => {
            return page.content.length === 0 ? null : page.content[0];
        }));
    }

    public aggregateInstallierteLeistung(groupBy: 'anlagentyp' | 'energieart',
        filter?: ErzeugungsanlageFilter): Observable<InstallierteLeistungAggregate[]> {
        let params = this.toHttpParams(null, filter);
        params = params.set('groupBy', groupBy);

        return this.http.get(environment.API_URL +
            '/erzeugungsanlage/aggregate/installierteLeistung', { params }) as Observable<InstallierteLeistungAggregate[]>;
    }

    private toHttpParams(pageable?: Pageable,
        filter?: ErzeugungsanlageFilter): HttpParams {
        let params = new HttpParams({
            fromObject: pageable || {} as any,
        });

        if (filter) {
            if (filter.westnetzId) {
                params = params.set('westnetzId', filter.westnetzId);
            }
            if (filter.stationsnamen) {
                params = params.set('stationsname', filter.stationsnamen);
            }
            if (filter.ortsnamen) {
                params = params.set('ortsname', filter.ortsnamen);
            }
            if (filter.netzzuordnungId) {
                params = params.set('netzzuordnungId', filter.netzzuordnungId);
            }
            if (filter.stadtwerkeId) {
                params = params.set('stadtwerkeId', filter.stadtwerkeId);
            }
            if (filter.anlagentype) {
                params = params.set('anlagentype', filter.anlagentype);
            }
            if (filter.anlagentypnummer) {
                params = params.set('anlagentypnummer', filter.anlagentypnummer);
            }
            if (typeof filter.installierteLeistungMin == 'number') {
                params = params.set('installierteLeistungMin', filter.installierteLeistungMin + '');
            }
            if (typeof filter.installierteLeistungMax == 'number') {
                params = params.set('installierteLeistungMax', filter.installierteLeistungMax + '');
            }
            if (filter.netzbetreiberName) {
                params = params.set('netzbetreiberName', filter.netzbetreiberName);
            }
            if (filter.netzbetreiberTeilgebiet) {
                params = params.set('netzbetreiberTeilgebiet', filter.netzbetreiberTeilgebiet);
            }
            if (filter.leitstellenRegion) {
                if (typeof filter.leitstellenRegion === 'string') {
                    params = params.set('leitstellenRegion', filter.leitstellenRegion);
                } else {
                    for (const r of filter.leitstellenRegion) {
                        params = params.append('leitstellenRegion', r);
                    }
                }
            }
            if (typeof filter.status == 'boolean') {
                params = params.set('status', JSON.stringify(filter.status));
            }
        }

        return params;
    }

    public getWestnetzId(stadtwerkeId?: string, westnetzId?: string): Observable<string[]> {
        let params = new HttpParams();

        if (stadtwerkeId) {
            params = params.set('stadtwerkeId', stadtwerkeId);
        }
        return this.http.get<string[]>(`${environment.API_URL}/erzeugungsanlage/westnetzid/${westnetzId || ''}`, { params });
    }
}



